import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { format } from "date-fns";
import { fr, enUS } from "date-fns/locale";
import Badge from "src/common/components/Badge/Badge";
import FilterChip from "src/common/components/FilterChip/FilterChip";
import Spinner from "src/common/components/Loader/Spinner";
import { ReleaseFeedEntry } from "src/model/model";
import ReleaseChanges from "src/features/releases/components/ReleaseChanges";
import { fetchReleaseFeed } from "src/features/releases/api/releasesApi";

/**
 * `/releases` — the full "What's new" timeline (Phase 8).
 *
 * A vertical, month-grouped feed of release entries. Surface filter chips drive
 * the `?surface=` query param; entries paginate 20-at-a-time with a load-more
 * button. Deep-links (`/releases#release-{id}`) scroll to and briefly highlight
 * the target card. Desktop-only — registered in the desktop Router tree.
 */

const PAGE_SIZE = 20;

type SurfaceFilter = "all" | "web" | "vr";

// Chip colors mirror the popover (features/releases/components/ReleaseBell.tsx)
// so the two surfaces read identically.
const surfaceChip: Record<ReleaseFeedEntry["surface"], { color: string; textColor: string }> = {
  web: { color: "bg-blue-100", textColor: "text-blue-700" },
  vr: { color: "bg-purple-100", textColor: "text-purple-700" },
  both: { color: "bg-green-100", textColor: "text-green-700" },
};

const capitalize = (s: string) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);

const Releases = () => {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const locale = i18n.language === "fr" ? fr : enUS;

  const [searchParams, setSearchParams] = useSearchParams();
  const surfaceParam = searchParams.get("surface");
  const surface: SurfaceFilter =
    surfaceParam === "web" || surfaceParam === "vr" ? surfaceParam : "all";

  const [entries, setEntries] = useState<ReleaseFeedEntry[]>([]);
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [highlightId, setHighlightId] = useState<number | null>(null);
  // Stores the last hash that was successfully scrolled to. An empty string means
  // no scroll has happened yet. Comparing against the current hash (not a boolean)
  // ensures each distinct deep-link navigation fires once while re-renders from
  // loadMore (same hash, entries change) don't trigger a second scroll.
  const didScrollRef = useRef("");
  // Holds a cancel function for any in-flight loadMore call; replaced on each
  // invocation and called by the initial-load cleanup when the surface changes.
  const loadMoreCancelRef = useRef<() => void>(() => undefined);

  // (Re)load the first page whenever the surface filter changes.
  useEffect(() => {
    let cancelled = false;
    // Cancel any loadMore fetch that was in-flight for the previous surface.
    loadMoreCancelRef.current();
    setInitialLoading(true);
    setEntries([]);
    setOffset(0);
    fetchReleaseFeed({
      limit: PAGE_SIZE,
      offset: 0,
      surface: surface === "all" ? undefined : surface,
    })
      .then((batch) => {
        if (cancelled) return;
        setEntries(batch);
        setHasMore(batch.length === PAGE_SIZE);
      })
      .catch(() => {
        if (cancelled) return;
        setEntries([]);
        setHasMore(false);
      })
      .finally(() => {
        if (!cancelled) setInitialLoading(false);
      });
    return () => {
      cancelled = true;
      loadMoreCancelRef.current();
    };
  }, [surface]);

  const loadMore = useCallback(() => {
    let cancelled = false;
    loadMoreCancelRef.current = () => {
      cancelled = true;
    };
    const nextOffset = offset + PAGE_SIZE;
    setLoadingMore(true);
    fetchReleaseFeed({
      limit: PAGE_SIZE,
      offset: nextOffset,
      surface: surface === "all" ? undefined : surface,
    })
      .then((batch) => {
        if (cancelled) return;
        setEntries((prev) => [...prev, ...batch]);
        setOffset(nextOffset);
        setHasMore(batch.length === PAGE_SIZE);
      })
      .catch(() => {
        if (cancelled) return;
        setHasMore(false);
      })
      .finally(() => {
        if (!cancelled) setLoadingMore(false);
      });
  }, [offset, surface]);

  // Deep-link: once the target card is in the DOM, scroll to it and flash a
  // highlight. Each distinct hash navigation gets one scroll attempt; re-renders
  // from loadMore (entries change, same hash) do not trigger a second scroll.
  useEffect(() => {
    if (didScrollRef.current === location.hash) return undefined;
    const match = location.hash.match(/^#release-(\d+)$/);
    if (!match) return undefined;
    const id = Number(match[1]);
    const el = document.getElementById(`release-${id}`);
    if (!el) return undefined;
    didScrollRef.current = location.hash;
    el.scrollIntoView({ behavior: "smooth", block: "center" });
    setHighlightId(id);
    const timeout = setTimeout(() => setHighlightId(null), 2000);
    return () => clearTimeout(timeout);
  }, [location.hash, entries]);

  // Group the (already DESC-sorted) entries into consecutive month buckets.
  const groups = useMemo(() => {
    const out: { label: string; items: ReleaseFeedEntry[] }[] = [];
    entries.forEach((entry) => {
      const label = entry.published_at_user_feed
        ? capitalize(format(new Date(entry.published_at_user_feed), "MMMM yyyy", { locale }))
        : "";
      const last = out[out.length - 1];
      if (last && last.label === label) last.items.push(entry);
      else out.push({ label, items: [entry] });
    });
    return out;
  }, [entries, locale]);

  // The newest entry of each surface is that surface's "current version". Entries
  // arrive DESC-sorted from offset 0, so the first occurrence of a surface value
  // is its latest; recomputed as more pages load.
  const currentBySurface = useMemo(() => {
    const seen = new Set<ReleaseFeedEntry["surface"]>();
    const ids = new Set<number>();
    entries.forEach((entry) => {
      if (!seen.has(entry.surface)) {
        seen.add(entry.surface);
        ids.add(entry.id);
      }
    });
    return ids;
  }, [entries]);

  const setSurface = (next: SurfaceFilter) => {
    if (next === "all") setSearchParams({});
    else setSearchParams({ surface: next });
  };

  const filters: SurfaceFilter[] = ["all", "web", "vr"];

  return (
    <>
      <header className="bg-white shadow sticky top-0 z-50">
        <div className="mx-auto py-6 px-4 ml-4">
          <h1 className="text-3xl font-bold text-black">{t("pages.releases.title")}</h1>
        </div>
      </header>

      <div className="w-full h-full max-w-9xl mx-auto pt-4 px-4 sm:px-6 lg:px-8 overflow-y-auto personalize-scroll-visible">
        {/* Surface filter chips → ?surface= param */}
        <div className="flex gap-2 mb-6">
          {filters.map((f) => (
            <FilterChip
              key={f}
              label={t(`pages.releases.filter.${f}`)}
              active={surface === f}
              onClick={() => setSurface(f)}
            />
          ))}
        </div>

        {initialLoading ? (
          <div className="relative h-40">
            <Spinner />
          </div>
        ) : entries.length === 0 ? (
          <p className="text-center text-gray-500 py-16">{t("pages.releases.empty")}</p>
        ) : (
          <div className="pb-12">
            {groups.map((group) => (
              <section key={group.label || "undated"} className="mb-10">
                <h2 className="text-lg font-semibold text-gray-500 sticky top-0 z-10 bg-slate-100 pt-2 pb-4">
                  {group.label}
                </h2>
                <div className="border-l-2 border-gray-200 pl-6 space-y-6">
                  {group.items.map((entry) => (
                    <article
                      key={entry.id}
                      id={`release-${entry.id}`}
                      className={`relative bg-white rounded-lg shadow p-6 transition-shadow scroll-mt-24 ${
                        highlightId === entry.id ? "ring-2 ring-inset ring-green-500" : ""
                      }`}
                    >
                      <div className="flex flex-wrap items-center gap-2 mb-2">
                        <Badge
                          color={surfaceChip[entry.surface].color}
                          textColor={surfaceChip[entry.surface].textColor}
                          text={t(`pages.releases.surface.${entry.surface}`)}
                        />
                        {currentBySurface.has(entry.id) && (
                          <span
                            title={t("pages.releases.current")}
                            aria-label={t("pages.releases.current")}
                            className="inline-block h-2 w-2 shrink-0 rounded-full bg-green-500"
                          />
                        )}
                        {entry.is_mandatory_update && (
                          <Badge
                            color="bg-green-100"
                            textColor="text-green-700"
                            text={t("pages.releases.mandatory")}
                            title={t("pages.releases.mandatoryTooltip")}
                          />
                        )}
                        {entry.version && (
                          <span className="text-sm text-gray-400">
                            {entry.version}
                            {entry.linked_unity_release?.version
                              ? ` (Unity ${entry.linked_unity_release.version})`
                              : ""}
                          </span>
                        )}
                        {entry.published_at_user_feed && (
                          <span className="ml-auto text-sm text-gray-400">
                            {format(new Date(entry.published_at_user_feed), "d MMMM yyyy", {
                              locale,
                            })}
                          </span>
                        )}
                      </div>

                      <h3 className="text-xl font-bold text-black mb-3 break-words">
                        {entry.headline}
                      </h3>

                      <ReleaseChanges html={entry.changes_html} />

                      {entry.optional_release_notice && (
                        <p className="mt-3 text-sm text-gray-600 break-words">
                          {entry.optional_release_notice}
                        </p>
                      )}
                    </article>
                  ))}
                </div>
              </section>
            ))}

            {hasMore && (
              <div className="flex justify-center mt-4">
                <button
                  type="button"
                  onClick={loadMore}
                  disabled={loadingMore}
                  className="btn-primary-outline"
                >
                  {loadingMore ? t("pages.releases.loading") : t("pages.releases.loadMore")}
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </>
  );
};

export default Releases;
